import React, { useState, useEffect } from 'react';
import { Lock, Unlock, User, X, LogOut, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { FadeInImage } from '../common/FadeInImage';
import { auth } from '../../lib/firebase';
import { signOut } from 'firebase/auth';

interface StaffMember {
  email: string;
  pin?: string;
  role: string;
  name?: string;
  photoUrl?: string;
}

interface PosLockScreenProps {
  staffList: StaffMember[];
  onUnlock: (email: string) => void;
}

export function PosLockScreen({ staffList, onUnlock }: PosLockScreenProps) {
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [pinInput, setPinInput] = useState('');
  const [error, setError] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);

  const handleKeyPress = (num: string) => {
    setError(false);
    if (pinInput.length < 4) {
      const newPin = pinInput + num;
      setPinInput(newPin);
      
      if (newPin.length === 4) {
        verifyPin(newPin);
      }
    }
  };

  const handleDelete = () => {
    setPinInput(prev => prev.slice(0, -1));
    setError(false);
  };

  const verifyPin = (enteredPin: string) => {
    if (selectedStaff?.pin === enteredPin) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('posActiveStaffName', selectedStaff.name);
      }
      onUnlock(selectedStaff.email);
    } else {
      setError(true);
      setTimeout(() => {
        setPinInput('');
        setError(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (!selectedStaff || showRecoveryModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      } else if (e.key === 'Escape') {
        setSelectedStaff(null);
        setPinInput('');
        setError(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedStaff, showRecoveryModal, pinInput]);

  return (
    <div className="fixed inset-0 z-[99999] bg-slate-950/40 backdrop-blur-3xl flex flex-col items-center justify-center p-4">
      <div className="max-w-4xl w-full flex flex-col items-center">
        
        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-theme-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-theme-primary/30 shadow-[0_0_30px_rgba(var(--glow-rgb),0.3)]">
            <Lock className="theme-primary-text" size={32} />
          </div>
          <h2 className="text-2xl font-black uppercase tracking-widest text-white">Acceso Restringido</h2>
          <p className="text-white/50 text-sm mt-2">Selecciona tu usuario e ingresa tu PIN</p>
        </div>

        {!selectedStaff ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-4 w-full max-w-4xl"
          >
            {staffList.map((staff) => (
              <button
                key={staff.email}
                onClick={() => {
                  if (!staff.pin) {
                    if (typeof window !== 'undefined') {
                      window.sessionStorage.setItem('posActiveStaffName', staff.name);
                    }
                    onUnlock(staff.email);
                  } else {
                    setSelectedStaff(staff);
                  }
                }}
                className="w-40 sm:w-48 bg-white/5 border border-white/10 hover:border-theme-primary/50 hover:bg-theme-primary/10 rounded-2xl p-6 flex flex-col items-center gap-4 transition-all hover:scale-105 group"
              >
                <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center group-hover:bg-theme-primary/20 transition-colors overflow-hidden border border-white/5">
                  {staff.photoUrl ? (
                    <FadeInImage src={staff.photoUrl} alt={staff.name || 'User'} className="w-full h-full object-cover" />
                  ) : (
                    <User size={24} className="text-white/40 group-hover:theme-primary-text" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-white truncate max-w-[120px]">{staff.name || staff.email.split('@')[0]}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">{staff.role}</p>
                </div>
              </button>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/80 border border-white/10 p-8 rounded-3xl w-full max-w-sm flex flex-col items-center relative overflow-hidden"
          >
            {/* Regresar */}
            <button 
              onClick={() => { setSelectedStaff(null); setPinInput(''); setError(false); }}
              className="absolute top-4 left-4 text-white/40 hover:text-white"
            >
              <X size={20} />
            </button>

            <div className="w-16 h-16 rounded-full bg-theme-primary/20 flex items-center justify-center mb-2 overflow-hidden border-2 border-theme-primary/50 shadow-lg">
              {selectedStaff.photoUrl ? (
                <FadeInImage src={selectedStaff.photoUrl} alt={selectedStaff.name || 'User'} className="w-full h-full object-cover" />
              ) : (
                <User size={24} className="theme-primary-text" />
              )}
            </div>
            <h3 className="text-lg font-black text-white mb-6">{selectedStaff.name || selectedStaff.email.split('@')[0]}</h3>

            {/* Puntos del PIN */}
            <div className={`flex gap-4 mb-8 transition-transform ${error ? 'animate-shake' : ''}`}>
              {[0, 1, 2, 3].map(i => (
                <div 
                  key={i} 
                  className={`w-5 h-5 rounded-full border-2 transition-all duration-75 ${
                    i < pinInput.length 
                      ? error ? 'bg-red-500 border-red-500 scale-110' : 'bg-amber-400 border-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.6)] scale-110' 
                      : 'border-white/20 bg-transparent scale-100'
                  }`} 
                />
              ))}
            </div>

            {error && <p className="text-red-400 text-xs font-bold absolute top-32">PIN Incorrecto</p>}

            {/* Teclado */}
            <div className="grid grid-cols-3 gap-3 w-full">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button
                  key={num}
                  onClick={() => handleKeyPress(num.toString())}
                  className="h-14 bg-white/5 hover:bg-white/10 active:bg-theme-primary/20 rounded-2xl text-xl font-bold text-white transition-colors"
                >
                  {num}
                </button>
              ))}
              <div />
              <button
                onClick={() => handleKeyPress('0')}
                className="h-14 bg-white/5 hover:bg-white/10 active:bg-theme-primary/20 rounded-2xl text-xl font-bold text-white transition-colors"
              >
                0
              </button>
              <button
                onClick={handleDelete}
                className="h-14 flex items-center justify-center text-white/50 hover:text-white active:text-red-400 transition-colors"
              >
                BORRAR
              </button>
            </div>

            {selectedStaff.role === 'admin' && (
              <button
                onClick={() => setShowRecoveryModal(true)}
                className="mt-6 text-sm text-white/40 hover:text-white transition-colors flex items-center gap-2 font-medium"
              >
                <HelpCircle size={16} />
                ¿Olvidaste tu PIN?
              </button>
            )}

            <AnimatePresence>
              {showRecoveryModal && <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute inset-0 bg-zinc-900 z-50 p-8 flex flex-col items-center justify-center text-center rounded-3xl"
                  >
                    <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-6 border border-red-500/20">
                      <LogOut className="text-red-500" size={24} />
                    </div>
                    <h4 className="text-xl font-black text-white mb-3">Recuperar Acceso</h4>
                    <p className="text-sm text-white/60 mb-8 leading-relaxed">
                      Por seguridad, debes volver a autenticarte. Cerraremos tu sesión actual. Al volver a entrar, ingresarás directo al panel y podrás configurar un PIN nuevo.
                    </p>
                    
                    <div className="flex flex-col gap-3 w-full mt-auto">
                      <button
                        onClick={() => signOut(auth)}
                        className="w-full bg-amber-400 text-black font-black uppercase tracking-wider py-4 rounded-xl hover:bg-amber-300 transition-colors shadow-[0_0_20px_rgba(251,191,36,0.2)]"
                      >
                        Cerrar Sesión
                      </button>
                      <button
                        onClick={() => setShowRecoveryModal(false)}
                        className="w-full bg-white/5 text-white font-bold uppercase tracking-wider py-4 rounded-xl hover:bg-white/10 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </motion.div>
              }
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}
